import {
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { IconRegistryService } from '../shared/services/icon-registry.service';
import { Industries } from '../shared/util/industries';
import { MapBoxService } from './mapbox/mapbox.service';
import { LayerStoreService } from './mapbox/services/layer-store.service';
import { BreakpointObserverService } from '../shared/services/breakpoint-observer.service';
import { LayersMenuService } from './menu/right-menu/layers-menu/layers-menu.service';
import { PlotlyViaCDNModule } from 'angular-plotly.js';
import { UserAccessService } from '../user/access/user-access.service';
import { UserAgentService } from '../shared/services/user-agent.service';
import { Meta, Title } from '@angular/platform-browser';
import { mapPageTags } from '../shared/util/seo-tags';
import { ENTERPRISE } from '../user/authentication.service';
import { SubscriptionPlans } from '../user/user/user.model';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { FavoritesComponent } from './favorites/favorites.component';
import { MapSearchComponent } from './map-search/map-search.component';
import { LevelSelectionComponent } from './mapbox/visualization/level-selection/level-selection.component';
import { SelectionToolComponent } from './mapbox/visualization/selection-tool/selection-tool.component';
import { MultipleFeaturesSelectionComponent } from './mapbox/visualization/selection-tool/multiple-features-selection/multiple-features-selection.component';
import { CommonColorscaleComponent } from './common-colorscale/common-colorscale.component';
import { MapboxComponent } from './mapbox/mapbox.component';
import { MapMenuComponent } from './menu/right-menu/map-menu/map-menu.component';
import { LayerNameFormatPipe } from './common-colorscale/layer-name-format.pipe';
import { SidenavComponent } from '../shared/components/sidenav/sidenav.component';
import { ModalService } from '../shared/services/modal.service';
import { GoogleAnalyticsService } from '../user/google-analytics.service';
import { MenuSearchService } from './menu/right-menu/layers-menu/menu-search/menu-search.service';
import { UserGuideComponent } from './user-guide/user-guide.component';

export const MAPS_GROUP = 'MAPS_GROUP';
export const CHARTS_GROUP = 'CHARTS_GROUP';
export const PLACES_GROUP = 'PLACES_GROUP';
export const ALL_DATA_GROUP = 'ALL_DATA_GROUP';

export type MenuGroupType =
  | typeof MAPS_GROUP
  | typeof CHARTS_GROUP
  | typeof PLACES_GROUP
  | typeof ALL_DATA_GROUP;

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  imports: [
    NgClass,
    FavoritesComponent,
    NgIf,
    MapSearchComponent,
    LevelSelectionComponent,
    SelectionToolComponent,
    AsyncPipe,
    MultipleFeaturesSelectionComponent,
    CommonColorscaleComponent,
    MapboxComponent,
    MapMenuComponent,
    LayerNameFormatPipe,
    SidenavComponent,
    UserGuideComponent,
  ],
})
export class MapComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  public isMobile: boolean = false;

  public mobileToolsOpened: boolean = false;
  public showToolsMobile: boolean = false;

  public activeMenuGroup: MenuGroupType = ALL_DATA_GROUP;

  @ViewChild(SidenavComponent) rightMenu!: SidenavComponent;

  @Output() readonly showSearchInput = new EventEmitter<boolean>();

  industries!: any;

  public isSearchInputActive: boolean = false;

  public isFavoritesVisible: boolean = false;

  constructor(
    private iconRegistry: IconRegistryService,
    private userService: UserAccessService,
    private mapboxService: MapBoxService,
    private breakpointObserverService: BreakpointObserverService,
    private modalService: ModalService,
    private userAgent: UserAgentService,
    public layerStore: LayerStoreService,
    public menuService: LayersMenuService,
    public accessService: UserAccessService,
    public menuSearchService: MenuSearchService, // used in html
    private meta: Meta,
    private title: Title,
    private gaService: GoogleAnalyticsService
  ) {
    this.iconRegistry.initMapIcons();
    this.industries = Industries;
  }

  ngOnInit(): void {
    this.title.setTitle(mapPageTags.title);
    this.meta.addTags(mapPageTags.tags);

    if (this.activeMenuGroup === ALL_DATA_GROUP) {
      this.loadPlotly();
    }

    this.breakpointObserverService.isMobile.subscribe((mobile) => {
      if (mobile) {
        this.isMobile = true;

        this.showToolsMobile = true;
      } else {
        this.isMobile = false;
        this.showToolsMobile = false;

        setTimeout(() => {
          this.toggleRightMenu();
        });
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public handleChartsGroupClick() {
    this.loadPlotly();
  }

  private loadPlotly() {
    if (this.isPlotlyLoaded()) {
      return;
    }
    PlotlyViaCDNModule.setPlotlyVersion('2.18.2');
    PlotlyViaCDNModule.setPlotlyBundle('finance');

    let firstTime = false;
    this.checkPlotlyLoaded().subscribe((isLoaded) => {
      if (!isLoaded) {
        setTimeout(() => this.loadPlotly(), 3000);
        if(!firstTime) {
          console.error('Failed to load Plotly, retrying...');
        }
        firstTime = true;
      }
    });
  }

  private isPlotlyLoaded(): boolean {
    //@ts-ignore
    return window['Plotly'];
  }

  private checkPlotlyLoaded(): Observable<boolean> {
    // Check if Plotly is loaded, if not we repeat loading and check
    return new Observable<boolean>((observer) => {
      const check = () => {
        //@ts-ignore
        if (this.isPlotlyLoaded()) {
          observer.next(true);
        } else {
          observer.next(false);
        }
      };
      setTimeout(check, 2000);
    });
  }

  public toggleRightMenu(): void {
    this.rightMenu.toggle().then((status: string) => {
      this.menuService.isMenuOpened = status !== 'close';
      this.mapboxService.resizeHomeMap();
    });
  }

  public openRightMenu(): void {
    this.rightMenu.open().then((status: string) => {
      this.menuService.isMenuOpened = status !== 'close';
      this.mapboxService.resizeHomeMap();
    });
  }

  public onSidenavClosed(): void {
    this.mapboxService.resizeHomeMap();
  }

  public handleSearchOpen(): void {
    // We have to check if the map is loaded before we can add geocoder as it's directly rely on map
    this.isSearchInputActive = this.mapboxService.map.loaded();
    if (this.isSearchInputActive) {
      this.isFavoritesVisible = false;
    }
  }

  public handleSearchClose(): void {
    // Check if map is moving as mapbox doesnt remove geocoder while moving, which leads to UI glitch
    // like angular is sure that geocoder is closed and we render button again,
    // but in the background is still mapbox geocoder input field
    this.isSearchInputActive = this.mapboxService.map.isMoving();
  }

  public handleFavoritesOpen(): void {
    this.isFavoritesVisible = !this.mapboxService.map.isMoving();

    if (this.isFavoritesVisible) {
      this.isSearchInputActive = false;
    }
  }

  public handleFavoritesClose(): void {
    this.isFavoritesVisible = false;
  }

  changeMenuGroup(group: MenuGroupType) {
    this.activeMenuGroup = group;
    this.gaService.logMenuGroupChanged(group);
  }

  userHasPaidPlan(): boolean {
    return this.userService.hasPaidPlan();
  }

  protected readonly MAPS_GROUP = MAPS_GROUP;
  protected readonly CHARTS_GROUP = CHARTS_GROUP;
  protected readonly PLACES_GROUP = PLACES_GROUP;
  protected readonly ENTERPRISE = ENTERPRISE;
  protected readonly SubscriptionPlans = SubscriptionPlans;
  protected readonly ALL_DATA_GROUP = ALL_DATA_GROUP;
}
